import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Cookie from "cookie-universal"; // Import Cookie for authentication
import { Axios } from "../../../Api/axios";
import { SOCIALLINKS } from "../../../Api/Api";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const cookie = Cookie();
const token = cookie.get("dashboardLogin"); // Token from cookie
const userRole = cookie.get("userRole"); // Role from cookie

export default function DataGridDemo() {
  const [alert, setAlert] = useState(null); // State to handle alerts
  const [alertVisible, setAlertVisible] = useState(true);

  const [rows, setRows] = useState([]); // State to store API data
  const [editData, setEditData] = useState({
    id: null,
    platform: "",
    icon: "",
    url: "",
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  // Fetch Social Links from API
  const fetchSocialLinks = async () => {
    try {
      const response = await Axios.get(`/panel/${SOCIALLINKS}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Role": userRole,
        },
      });

      const socialLinks = response.data.socialLinks || [];

      const formattedRows = socialLinks.map((link, index) => ({
        id: link.id, // Assuming each social link has a unique `id`
        platform: link.platform,
        icon: link.icon,
        url: link.url,
      }));

      setRows(formattedRows);
    } catch (error) {
      console.error("Error fetching social links:", error);
    }
  };

  // Handle Edit Action
  const handleEdit = (id) => {
    const socialLink = rows.find((row) => row.id === id);
    setEditData({
      id: socialLink.id,
      platform: socialLink.platform,
      icon: socialLink.icon,
      url: socialLink.url,
    });
    setIsEditing(true); // Open dialog
  };

  const handleUpdate = async () => {
    if (!window.confirm("Are you sure you want to update this link?")) return;

    try {
      const response = await Axios.put(
        `/panel/${SOCIALLINKS}/${editData.id}`,
        editData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-User-Role": userRole,
          },
        }
      );

      setAlert({
        message: "Social link updated successfully",
        severity: "success",
      });

      console.log(response.data);
      setRows(
        rows.map((row) =>
          row.id === editData.id
            ? {
                ...row,
                platform: editData.platform,
                icon: editData.icon,
                url: editData.url,
              }
            : row
        )
      );
      setIsEditing(false); // Close dialog
      setEditData({ id: null, platform: "", icon: "", url: "" });
      // Set alert visibility to fade out after 2 seconds
      setTimeout(() => setAlertVisible(false), 2000);
      setAlertVisible(true);
    } catch (error) {
      console.error("Error updating social link:", error);
      setAlert({
        error: "Error updating social link:",
        severity: "error",
      });

      setTimeout(() => setAlertVisible(false), 2000);
      setAlertVisible(true);
    }
  };

  // Handle Delete Action
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this link?")) return;

    try {
      await Axios.delete(`/panel/${SOCIALLINKS}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Role": userRole,
        },
      });

      setRows(rows.filter((row) => row.id !== id));
      setAlert({
        message: "Social link deleted successfully",
        severity: "success",
      });
      // Set alert visibility to fade out after 2 seconds
      setTimeout(() => setAlertVisible(false), 2000);
      setAlertVisible(true);
    } catch (error) {
      console.error("Error deleting social link:", error);
      setAlert({ message: "Error deleting social link", severity: "error" });
      setTimeout(() => setAlertVisible(false), 2000);
      setAlertVisible(true);
    }
  };

  // Define Columns for DataGrid
  const columns = [
    { field: "id", headerName: "ID", width: 50, resizable: false },
    { field: "platform", headerName: "Platform", width: 200, resizable: false },
    {
      field: "icon",
      headerName: "Icon",
      width: 150,
      resizable: false,
      renderCell: (params) =>
        params.value ? (
          <img src={params.value} alt="icon" width={30} />
        ) : (
          "No Icon"
        ),
    },
    {
      field: "url",
      headerName: "URL",
      width: 400,
      resizable: false,
      renderCell: (params) => (
        <a href={params.value} target="_blank" rel="noopener noreferrer">
          {params.value}
        </a>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      resizable: false,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleEdit(params.row.id)} color="primary">
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(params.row.id)} color="error">
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ height: 400, width: "100%" }}>
      {/* Display Alert if available */}
      {alert && alertVisible && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlertVisible(false)}
          sx={{
            transition: "opacity 1s ease-out",
            opacity: alertVisible ? 1 : 0,
          }}
        >
          {alert.message}
        </Alert>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
            },
          },
        }}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        disableColumnMenu
      />

      {/* Edit Social Link Dialog */}
      <Dialog open={isEditing} onClose={() => setIsEditing(false)}>
        <DialogTitle>Edit Social Link</DialogTitle>
        <DialogContent>
          <TextField
            label="Platform"
            fullWidth
            value={editData.platform}
            onChange={(e) =>
              setEditData({ ...editData, platform: e.target.value })
            }
            sx={{ mb: 2, mt: 2 }}
          />
          <TextField
            label="Icon URL"
            fullWidth
            value={editData.icon}
            onChange={(e) => setEditData({ ...editData, icon: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="URL"
            fullWidth
            value={editData.url}
            onChange={(e) => setEditData({ ...editData, url: e.target.value })}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditing(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
